import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  LockKeyhole,
  FileText,
  Shapes,
  GitBranch,
} from "lucide-react";
import { api, json } from "./api";
import { Brand, Spinner, ThemePicker } from "./components";
import type { User } from "./types";

export function Auth({
  setup,
  allowed,
  onLogin,
}: {
  setup: boolean;
  allowed: boolean;
  onLogin: (user: User) => void;
}) {
  const [username, setUsername] = useState(""),
    [password, setPassword] = useState(""),
    [repeat, setRepeat] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (setup && password !== repeat) {
      setError("Пароли не совпадают.");
      return;
    }
    setBusy(true);
    try {
      const result = await api<{ user: User }>(
        `/auth/${setup ? "setup" : "login"}`,
        json("POST", { username, password }),
      );
      onLogin(result.user);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="auth-page">
      <div className="auth-theme">
        <ThemePicker />
      </div>
      <section className="auth-story">
        <Brand />
        <div className="auth-copy">
          <span className="eyebrow">ЛИЧНАЯ МАСТЕРСКАЯ ГЕЙМДИЗАЙНЕРА</span>
          <h1>
            Дай идеям
            <br />
            игровую форму.
          </h1>
          <p>
            От первой заметки до проверенной механики.
            <br />
            Твои проекты и материалы в одном месте.
          </p>
          <div className="auth-diagram" aria-hidden="true">
            <div>
              <FileText size={22} />
              <span>Идея</span>
            </div>
            <span className="diagram-line" />
            <div>
              <Shapes size={22} />
              <span>Механика</span>
            </div>
            <span className="diagram-line" />
            <div>
              <GitBranch size={22} />
              <span>Прототип</span>
            </div>
          </div>
        </div>
        <div className="auth-footnote">
          Собственное пространство для будущих игр
        </div>
      </section>
      <section className="auth-form-area">
        <div className="auth-card">
          <span className="auth-lock">
            <LockKeyhole size={22} />
          </span>
          <h2>{setup ? "Начнём с твоего аккаунта" : "С возвращением"}</h2>
          <p className="muted">
            {setup
              ? "Создай аккаунт владельца. Проекты будут доступны только тебе."
              : "Войди, чтобы продолжить работу над проектами."}
          </p>
          {setup && !allowed ? (
            <div className="notice">
              Создай владельца на сервере командой <code>npm run owner</code>,
              затем обнови страницу.
            </div>
          ) : (
            <form onSubmit={submit}>
              <label>
                Логин
                <input
                  autoFocus
                  autoComplete="username"
                  required
                  minLength={setup ? 3 : 1}
                  maxLength={64}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Например, gamedesigner"
                />
              </label>
              <label>
                Пароль
                <input
                  type="password"
                  autoComplete={setup ? "new-password" : "current-password"}
                  minLength={setup ? 12 : 1}
                  maxLength={128}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={setup ? "Не менее 12 символов" : "Твой пароль"}
                />
              </label>
              {setup && (
                <label>
                  Повтори пароль
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                  />
                </label>
              )}
              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}
              <button className="primary full" disabled={busy}>
                {busy ? (
                  <Spinner />
                ) : (
                  <>
                    {setup ? "Создать мастерскую" : "Войти"}
                    <ArrowRight size={17} />
                  </>
                )}
              </button>
            </form>
          )}
          <p className="auth-note">
            Без публичной регистрации.
            <br />
            Данные хранятся на твоём сервере.
          </p>
        </div>
      </section>
    </div>
  );
}
